import { useCallback, useMemo, useRef, useState } from 'react';

import DisplayDuration from '@src/components/DisplayDuration';
import SorterColumn from '@src/components/Table/components/SorterColumn/SorterColumn';
import ExecutionStatus from '@src/components/Widgets/ExecutionStatus';
import { SCHEDULE_TYPES } from '@src/constants/commons';
import { EXECUTION_STATE } from '@src/models/execution';
import { DateUtil } from '@src/packages/utils/dates';
import { getDeliveryStateByUser } from '@src/packages/utils/functions/members';
import { getTriggerStatus } from '@src/packages/utils/functions/triggers';
import { UserUtil } from '@src/packages/utils/functions/users';
import { get, set } from '@src/packages/utils/storage';
import ExecutionService from '@src/services/ExecutionService';
import TriggerService from '@src/services/TriggerService';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { translateString } from '@src/util/intl/translateString';

import { camelize } from 'packages/utils';

import { useEventsByScheduleContext } from '../../../contexts/EventsByScheduleContext';
import { DEVIATIONS_FROM_TASK_COMPLIANCE_DATA_SOURCE } from '../../../forms/DeviationsFromTaskCompliance/constants';
import { useGenericTableData } from '../hooks/useGenericTableData';
import { widgetFilterValuesToColumnFilters } from '../utils/widgetFilterValuesToColumnFilters';

export const DEVIATIONS_SORT_STORAGE_KEY = 'deviationsSortTable';

const DEVIATIONS_SORT_FIELD_MAP = {
  createdAt: 'createdAt._seconds',
  status: 'currentState',
};

const toBackendSortFields = sorterFields =>
  Object.entries(sorterFields)
    .filter(([, v]) => v?.order)
    .map(([col, { order }]) => ({
      name: DEVIATIONS_SORT_FIELD_MAP[col] ?? col,
      order,
    }));

const toDefaultSorterValues = (sortFields = []) =>
  sortFields.map(({ name, order }) => ({
    name:
      Object.keys(DEVIATIONS_SORT_FIELD_MAP).find(k => DEVIATIONS_SORT_FIELD_MAP[k] === name) ??
      name,
    order,
  }));

const TableCellAction = ({ children, onClick }) => {
  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(event);
    }
  };

  return (
    <span
      role="button"
      tabIndex={0}
      className="ant-btn-link gx-pointer gx-mb-0 gx-px-0"
      style={{ fontSize: '13px' }}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </span>
  );
};

const UsersCell = ({ userIds, orgId, divisionId, executionId }) => {
  const executionService = new ExecutionService();
  const { drawerActions, DRAWER_TYPES, setLoading } = useEventsByScheduleContext();

  const handleOpenUsersDrawer = async () => {
    try {
      setLoading(true);

      const execution = await executionService.getExecutionByIdElastic(
        orgId,
        divisionId,
        executionId,
      );

      const users = UserUtil.getUsersFromMembers(execution?.schedule?.members);
      const userWithDeliveryState = users.map(user => {
        return {
          ...user,
          deliveryState: getDeliveryStateByUser(user.id, {
            viewed: execution?.viewed,
            delivered: execution?.delivered,
            deliveryState: execution?.deliveryState,
          }),
        };
      });

      drawerActions.open(DRAWER_TYPES.USERS, userWithDeliveryState);
    } catch (error) {
      console.error('Error opening users drawer ->>>', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <TableCellAction onClick={handleOpenUsersDrawer}>
      {translateString('schedules.list.users.see')} ({userIds?.length || 0})
    </TableCellAction>
  );
};

const EventsCell = ({ executionId, orgId, divisionId, triggersIds }) => {
  const { drawerActions, DRAWER_TYPES, setLoading } = useEventsByScheduleContext();

  const handleOpenEventsDrawer = async () => {
    try {
      setLoading(true);
      const triggerService = new TriggerService();
      const triggers = await triggerService.getTriggersByExecution(orgId, divisionId, executionId);
      const triggersForTable = triggers.map(trigger => {
        return {
          id: trigger.id,
          name: trigger.name,
          eventType: trigger.eventType,
          locations: trigger.locations,
          status: getTriggerStatus(trigger),
          executions: `${trigger.finishedExecutions}/${trigger.expectedExecutions}`,
        };
      });

      drawerActions.open(DRAWER_TYPES.EVENTS, triggersForTable);
    } catch (error) {
      console.error('Error opening events drawer ->>>', error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <TableCellAction onClick={handleOpenEventsDrawer}>
      {translateString('schedules.list.events.see')} ({triggersIds?.length || 0})
    </TableCellAction>
  );
};

const FormsCell = ({ value }) => {
  const { drawerActions, DRAWER_TYPES } = useEventsByScheduleContext();
  return (
    <TableCellAction onClick={() => drawerActions.open(DRAWER_TYPES.FORMS, value)}>
      {translateString('schedules.list.forms.see')} ({value?.length || 0})
    </TableCellAction>
  );
};

const DelaysCell = ({ count, divisionId, executionId }) => {
  const { drawerActions, DRAWER_TYPES } = useEventsByScheduleContext();

  return (
    <TableCellAction
      onClick={() =>
        drawerActions.open(DRAWER_TYPES.DELAYS, {
          divisionId,
          executionId,
        })
      }
    >
      {count}
    </TableCellAction>
  );
};

const COLUMN_RENDERERS = ({ key, orgId }) => {
  switch (key) {
    case 'startDelay':
      return value => (value ? <DisplayDuration minutes={value} /> : '-');
    case 'finalDuration':
      return value => (value ? <DisplayDuration minutes={value} /> : '-');
    case 'users':
      return (value, record) => (
        <UsersCell
          userIds={value}
          orgId={orgId}
          divisionId={record?.divisionId}
          executionId={record?.key}
        />
      );

    case 'events':
      return (value, record) => (
        <EventsCell
          executionId={record.key}
          orgId={orgId}
          divisionId={record?.divisionId}
          triggersIds={value}
        />
      );
    case 'forms':
      return value => <FormsCell value={value} />;
    case 'delays':
      return (value, record) => (
        <DelaysCell
          count={value}
          divisionId={record?.divisionId}
          executionId={record?.executionId}
        />
      );
    case 'totalDelayDuration':
      return value => (value && value > 0 ? <DisplayDuration minutes={value} /> : '-');
    case 'assignedDuration':
      return value => (value && value >= 0 ? <DisplayDuration minutes={value} /> : '-');
    case 'assignedRealDuration':
      return value => (value && value >= 0 ? <DisplayDuration minutes={value} /> : '-');
    case 'differenceDuration':
      return value => (value && value >= 0 ? <DisplayDuration minutes={value} /> : '-');
    case 'delayToAssignUsers':
      return value => (value && value >= 0 ? <DisplayDuration minutes={value} /> : '-');
    case 'status':
      return value => <ExecutionStatus status={value} />;
    default:
      return undefined;
  }
};

export const SEARCHABLE_COLUMNS_NAMES = {
  SCHEDULE_NAME: 'scheduleName',
  SCHEDULE_TASK_SUMMARY_NAME: 'scheduleTaskSummaryName',
};
export const SEARCHABLE_COLUMNS = [
  SEARCHABLE_COLUMNS_NAMES.SCHEDULE_NAME,
  SEARCHABLE_COLUMNS_NAMES.SCHEDULE_TASK_SUMMARY_NAME,
];

const RIGHT_ALIGNED_DEVIATION_COLUMNS = new Set([
  'startDelay',
  'finalDuration',
  'assignedDuration',
  'assignedRealDuration',
  'differenceDuration',
  'delayToAssignUsers',
  'delays',
  'totalDelayDuration',
]);

export const mergeDeviationColumnsWithDefaults = (columns = []) => {
  const configuredKeys = new Set(columns.map(column => column.key));
  const missingActiveColumns = DEVIATIONS_FROM_TASK_COMPLIANCE_DATA_SOURCE.filter(
    column => column.isActive && !configuredKeys.has(column.key),
  );

  return [...columns, ...missingActiveColumns].sort(
    (first, second) => (first.order ?? 0) - (second.order ?? 0),
  );
};

export const buildDeviationsColumns = (
  rawData,
  { queryParams, orgId, sortFields = [], onSortFieldsChange, widgetId },
) => {
  if (!rawData) return [];
  const columns = mergeDeviationColumnsWithDefaults(queryParams?.columns);
  const activeColumns = columns.filter(item => item?.isActive);

  return activeColumns.map(item => {
    const columnName = DEVIATIONS_FROM_TASK_COMPLIANCE_DATA_SOURCE.find(
      data => data.key === item.key,
    )?.name;

    let columnNameComponent = columnName;
    if (Object.keys(DEVIATIONS_SORT_FIELD_MAP).includes(item.key)) {
      columnNameComponent = (
        <div className="gx-flex-row gx-guarnic-gap-2 gx-align-items-center">
          {columnName}
          <SorterColumn
            column={item.key}
            onChange={newSortFields => {
              const backendFormat = toBackendSortFields(newSortFields);
              const updated = {
                ...get(DEVIATIONS_SORT_STORAGE_KEY),
                [widgetId]: { sortFields: backendFormat },
              };
              set(DEVIATIONS_SORT_STORAGE_KEY, updated);
              onSortFieldsChange(updated);
            }}
            defaultValues={toDefaultSorterValues(sortFields)}
          />
        </div>
      );
    }
    return {
      title: columnNameComponent,
      dataIndex: item.key,
      key: item.key || item.name,
      align: RIGHT_ALIGNED_DEVIATION_COLUMNS.has(item.key) ? 'right' : 'left',
      searchable: SEARCHABLE_COLUMNS.includes(item.key),
      searchPlaceholder: translateString('general.search'),
      render: COLUMN_RENDERERS({ key: item.key, orgId }),
    };
  });
};

/** Duration of the delay that starts at `index`, or null when it has no closing
 *  timestamp. Elastic freezes `endAt` on a still-open delay at the last record save,
 *  so a close is usually present even while the task is delayed. */
const getStalledDurationMs = (states, index) => {
  const startMs = DateUtil.getTimestampMs(states[index]?.startAt);
  const exitState = states
    .slice(index + 1)
    .find(next => next.fromState === EXECUTION_STATE.stalled);
  const endMs =
    DateUtil.getTimestampMs(states[index]?.endAt) ?? DateUtil.getTimestampMs(exitState?.startAt);

  if (!startMs || !endMs) return null;

  return Math.max(0, endMs - startMs);
};

/** Total time the task spent delayed, in minutes, counting the delay in progress as
 *  the backend does. Prefers the precomputed metric; execution_records older than that
 *  field were never backfilled, so their total is rebuilt from the state history. */
const getTotalDelayMinutes = (recordInfo, statesSorted) => {
  const precomputedMs = recordInfo?.totalStalledDurationMs;
  if (typeof precomputedMs === 'number') return precomputedMs / 60000;

  const states = statesSorted || [];
  const totalMs = states.reduce(
    (total, state, index) =>
      state.toState === EXECUTION_STATE.stalled
        ? total + (getStalledDurationMs(states, index) ?? 0)
        : total,
    0,
  );

  return totalMs / 60000;
};

export const buildDeviationsDataSource = rawData => {
  const { data: agg } = camelize({ data: rawData });

  if (!agg) return [];

  const results = agg?.aggregations?.byRecord;
  const buckets = results?.buckets || [];

  return buckets.map((bucket, index) => {
    const key = bucket.key || index;
    const recordInfo = bucket.recordInfo.hits.hits[0].source;
    const states = recordInfo?.states;
    const statesSorted = states?.sort(
      (a, b) => DateUtil.getTimestampMs(a.startAt) - DateUtil.getTimestampMs(b.startAt),
    );
    const firstState = statesSorted?.[0];

    /* Build source data */
    const templateName = recordInfo?.scheduleTaskSummaryName || '-';
    const taskName = recordInfo?.scheduleName || '-';
    const createdAt = DateUtil.convertTimestampToDate(firstState?.startAt, 'DD/MM/YYYY HH:mm');

    const status = recordInfo?.currentState;
    const stateCompleted = states?.find(
      state => state.toState === EXECUTION_STATE.completed,
    )?.startAt;

    /* Timestamps for calculations */
    const startAtMs = DateUtil.getTimestampMs(recordInfo?.startAt);
    const endAtMs = DateUtil.getTimestampMs(recordInfo?.endAt);
    const triggerFirstExecutedAtMs = DateUtil.getTimestampMs(recordInfo?.triggerFirstExecutedAt);
    const stateCompletedMs = DateUtil.getTimestampMs(stateCompleted);

    /* Assigned duration (scheduled end - scheduled start) */
    const assignedDurationMinutes = startAtMs && endAtMs ? (endAtMs - startAtMs) / 60000 : null;

    /* Real duration (actual completion - actual start) */
    const assignedRealDurationMinutes =
      triggerFirstExecutedAtMs && stateCompletedMs
        ? (stateCompletedMs - triggerFirstExecutedAtMs) / 60000
        : null;

    /* Difference */
    const differenceDuration =
      assignedDurationMinutes !== null && assignedRealDurationMinutes !== null
        ? assignedDurationMinutes - assignedRealDurationMinutes
        : null;

    /* Formatted dates for display */
    const startDate = DateUtil.convertTimestampToDate(recordInfo?.startAt, 'DD/MM/YYYY HH:mm');
    const endDate = DateUtil.convertTimestampToDate(recordInfo?.endAt, 'DD/MM/YYYY HH:mm');
    const start =
      DateUtil.convertTimestampToDate(recordInfo?.triggerFirstExecutedAt, 'DD/MM/YYYY HH:mm') ||
      '-';
    const startInMs = DateUtil.getTimestampMs(recordInfo?.triggerFirstExecutedAt);
    const end = DateUtil.convertTimestampToDate(stateCompleted, 'DD/MM/YYYY HH:mm') || '-';
    const assignedAt =
      DateUtil.convertTimestampToDate(recordInfo?.assignedAt, 'DD/MM/YYYY HH:mm') || '-';

    const assignedAtInMs = DateUtil.getTimestampMs(recordInfo?.assignedAt);
    const startDateInMs = DateUtil.getTimestampMs(recordInfo?.startAt);
    const delayToAssignUsers =
      assignedAtInMs && startDateInMs ? (assignedAtInMs - startDateInMs) / 60000 : null;

    const startDelay = startDateInMs && startInMs ? (startInMs - startDateInMs) / 60000 : null; // NOTE: it was changed since it doesn't make sense to use (stateDurations?.active || 0) / 60;

    const users = recordInfo?.usersWithAccess || [];
    const forms = recordInfo?.forms || [];
    const triggers = recordInfo?.triggersIds || [];
    const delays = states?.filter(state => state.toState === EXECUTION_STATE.stalled).length || 0;
    const totalDelayDuration = getTotalDelayMinutes(recordInfo, statesSorted);

    return {
      key,
      executionId: recordInfo?.id || key,
      scheduleName: taskName,
      scheduleTaskSummaryName: templateName,
      createdAt,
      startDate,
      start,
      startDelay,
      endDate,
      end,
      assignedDuration: assignedDurationMinutes,
      assignedRealDuration: assignedRealDurationMinutes,
      differenceDuration,
      forms,
      users,
      events: triggers,
      status,
      delays,
      totalDelayDuration,
      divisionId: recordInfo?.divisionId,
      assignedAt,
      delayToAssignUsers,
    };
  });
};

export const buildDeviationsParams = (
  queryParams,
  commonParams,
  { sortFields = [], columnFilters = {} } = {},
) => ({
  ...commonParams,
  divisionIds: queryParams.divisionIds || commonParams.divisionIds,
  templateIds: queryParams.templateIds,
  scheduleType: SCHEDULE_TYPES.task,
  ...(sortFields.length > 0 && { sortFields }),
  ...(Object.keys(columnFilters).length > 0 && { columnFilters }),
});

export const getDeviationsTotalEvents = data => {
  const { data: agg } = camelize({ data });
  return agg?.hits?.total?.value || 0;
};

export const useDeviationsTableData = props => {
  const [sortFieldsByWidget, setSortFieldsByWidget] = useState(
    () => get(DEVIATIONS_SORT_STORAGE_KEY) ?? {},
  );
  const columnSearchActionsRef = useRef();

  // const { columnFilters } = useColumnSearch({
  //   onColumnSearch: () => {
  //     columnSearchActionsRef.current?.resetPage?.();
  //   },
  //   storageKey: props.widget?.id ? `columnFilters:${props.widget.id}` : undefined,
  // });

  const onTableChange = useCallback(
    (pagination, tableFilters) => {
      props.widgetTableFilterHandlers?.applyWidgetTableFiltersBatch?.(pagination, tableFilters);
    },
    [props.widgetTableFilterHandlers],
  );

  const sortFieldsMemo = useMemo(
    () => sortFieldsByWidget[props.widget?.id]?.sortFields || [],
    [sortFieldsByWidget, props.widget?.id],
  );

  const buildParamsWithSort = useCallback(
    (queryParams, commonParams) =>
      buildDeviationsParams(queryParams, commonParams, {
        sortFields: sortFieldsMemo,
        columnFilters: commonParams.columnFilters,
      }),
    [sortFieldsMemo],
  );

  const buildColumnsWithSort = useCallback(
    (rawData, context) =>
      buildDeviationsColumns(rawData, {
        ...context,
        sortFields: sortFieldsMemo,
        onSortFieldsChange: setSortFieldsByWidget,
        widgetId: props.widget?.id,
      }),
    [sortFieldsMemo, props.widget?.id],
  );

  const columnFilters = useMemo(
    () => widgetFilterValuesToColumnFilters(props?.widgetFilterValues),
    [props?.widgetFilterValues],
  );

  return useGenericTableData({
    ...props,
    op: API_OPERATIONS.EXECUTIONS_RECORDS_DASHBOARD,
    buildParams: buildParamsWithSort,
    buildColumns: buildColumnsWithSort,
    buildDataSource: buildDeviationsDataSource,
    getTotalEvents: getDeviationsTotalEvents,
    additionalDependencies: [JSON.stringify(sortFieldsMemo)],
    columnFilters,
    columnSearchActionsRef,
    columnSearchHandlers: {
      onTableChange,
    },
  });
};
